import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEndpointTestRunGroup } from '@/hooks/useEndpointTestRunGroups';
import { useEndpointTests } from '@/hooks/useEndpointTests';
import { TrendDots } from './TrendDots';
import { TestResultDrawer } from './TestResultDrawer';
import {
  formatDuration,
  resultDisplay,
  resultDuration,
  sortResults,
  TONE_DOT_CLASS,
} from '@/utils/testRunResults';

interface TestRunDetailProps {
  endpointId: string;
  testRunId: string;
}

/**
 * The results of one open test run (task 1177): one row per result — status
 * dot, test name, duration, label, trend — failures first. Clicking a row opens
 * its drawer. The detail is polled until every result settles, so a test run
 * fills in as results arrive.
 */
export function TestRunDetail({ endpointId, testRunId }: TestRunDetailProps) {
  const { data, isLoading, isError } = useEndpointTestRunGroup(testRunId);
  const { data: testsData } = useEndpointTests(endpointId);
  const [openResultId, setOpenResultId] = useState<string | null>(null);

  if (isLoading) return <p className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">Loading results…</p>;
  if (isError || !data) return <p className="px-4 py-2 text-xs text-red-500">Couldn’t load this test.</p>;
  const refusals = data.refusals ?? [];
  if (data.results.length === 0 && refusals.length === 0) {
    return <p className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">No results yet — scenarios are running.</p>;
  }
  const scenarioName = (id: string) => testsData?.data?.find((test) => test.id === id)?.name ?? 'Deleted scenario';

  return (
    <ul data-testid={`test-run-results-${testRunId}`} className="border-t border-gray-100 dark:border-gray-700">
      {/* Refused before a collaboration started (task 1229): no result, the service's reason instead. */}
      {refusals.map((refusal) => (
        <li
          key={`refused-${refusal.testId}`}
          data-testid={`refused-row-${refusal.testId}`}
          className="flex items-center gap-2 px-4 py-2 text-sm border-b border-gray-100 dark:border-gray-700"
        >
          <span className="w-2 h-2 rounded-full shrink-0 bg-gray-300 dark:bg-gray-500" />
          <span className="truncate text-gray-900 dark:text-white">{scenarioName(refusal.testId)}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">not run</span>
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate" title={refusal.error}>
            {refusal.error}
          </span>
        </li>
      ))}
      {sortResults(data.results).map((result) => {
        const display = resultDisplay(result);
        const open = openResultId === result.id;
        const name = result.endpointTest?.name ?? 'Deleted scenario';
        return (
          <li key={result.id} data-testid={`result-row-${result.id}`} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
            <div className="flex items-center gap-2 px-4 py-2 text-sm">
              <span className={`w-2 h-2 rounded-full shrink-0 ${TONE_DOT_CLASS[display.tone]}`} />
              <button
                type="button"
                onClick={() => setOpenResultId(open ? null : result.id)}
                aria-expanded={open}
                className="flex items-center gap-1 min-w-0 text-left text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                {open ? <ChevronDownIcon className="h-3.5 w-3.5 shrink-0" /> : <ChevronRightIcon className="h-3.5 w-3.5 shrink-0" />}
                <span className="truncate">{name}</span>
              </button>
              <span
                className={`text-xs ${
                  display.tone === 'fail' || display.tone === 'errored'
                    ? 'text-red-600 dark:text-red-400'
                    : display.tone === 'pass'
                      ? 'text-green-700 dark:text-green-400'
                      : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {display.label}
              </span>
              {display.human && (
                <span className="px-1 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200">
                  human
                </span>
              )}
              {display.note && <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{display.note}</span>}
              <span className="ml-auto flex items-center gap-3 shrink-0">
                <TrendDots endpointTestId={result.endpointTestId} />
                <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  {formatDuration(resultDuration(result))}
                </span>
              </span>
            </div>
            {open && (
              <TestResultDrawer
                result={result}
                endpointId={endpointId}
                endpointTestRunGroupId={testRunId}
                testVersion={data.resourceVersion ?? null}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default TestRunDetail;
